'use client';
import React, { useMemo } from 'react';
import Link from 'next/link';
import { HomepagePartnerItem, defaultHomepageConfig } from '@/lib/homepage-config';

export default function PartnerSection({
  title = defaultHomepageConfig.partnerSection.title,
  items = defaultHomepageConfig.partnerSection.items,
}: {
  title?: string;
  items?: HomepagePartnerItem[];
}) {
  const logos = useMemo(() => {
    const active = items.filter((item) => item.enabled).sort((a, b) => a.sort_order - b.sort_order);
    return active.length ? active : defaultHomepageConfig.partnerSection.items;
  }, [items]);
  const partnerCount = logos.length;

  return (
    <section className="py-8">
      <div className="max-w-[1500px] mx-auto px-4">
        <div className="overflow-hidden rounded-none border border-slate-200 bg-white shadow-sm">
          <div className="grid border-b border-slate-200 bg-slate-950 text-white lg:grid-cols-[1fr_360px]">
            <div className="px-4 py-5 sm:px-6">
              <p className="mb-2 text-[11px] font-black uppercase tracking-[0.22em] text-amber-300">
                HỢP TÁC CHÍNH HÃNG
              </p>
              <div className="flex items-center">
                <div className="mr-3 h-7 w-1.5 bg-amber-400" />
                <h2 className="text-2xl font-black tracking-tight text-white md:text-3xl">
                  {title}
                </h2>
              </div>
              <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-300">
                DN TECH chọn lọc các thương hiệu linh kiện lớn để đảm bảo nguồn hàng ổn định, sản
                phẩm chính hãng và quy trình bảo hành rõ ràng cho khách hàng.
              </p>
              <div className="mt-5 grid gap-2 text-xs font-semibold text-slate-200 sm:grid-cols-3">
                <div className="border border-white/10 bg-white/5 px-3 py-2">
                  Nguồn hàng rõ ràng
                </div>
                <div className="border border-white/10 bg-white/5 px-3 py-2">
                  Bảo hành minh bạch
                </div>
                <div className="border border-white/10 bg-white/5 px-3 py-2">
                  Tư vấn đúng nhu cầu
                </div>
              </div>
            </div>

            <div className="flex border-t border-white/10 bg-white/[0.03] px-4 py-5 lg:border-l lg:border-t-0 lg:px-6">
              <div className="flex w-full items-center justify-between gap-5 lg:flex-col lg:items-start lg:justify-center">
                <div>
                  <div className="text-5xl font-black leading-none text-amber-300">
                    {partnerCount}
                  </div>
                  <div className="mt-2 text-sm font-bold text-white">đối tác tiêu biểu</div>
                </div>
                <div className="border border-amber-300/40 bg-amber-300 px-3 py-2 text-xs font-black uppercase tracking-[0.12em] text-slate-950">
                  Chính hãng
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-px bg-slate-200 p-px md:grid-cols-3">
            {logos.map((logo, index) => (
              <Link
                key={`${logo.alt}-${index}`}
                href={logo.href}
                className="group flex min-h-[156px] flex-col justify-between bg-white p-5"
              >
                <div className="flex items-start justify-between gap-4">
                  <span className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">
                    Partner {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-emerald-700">
                    Active
                  </span>
                </div>

                <div className="flex h-20 items-center justify-center">
                  {logo.src ? (
                    <img
                      src={logo.src}
                      alt={logo.alt}
                      className="max-h-14 max-w-[210px] object-contain"
                    />
                  ) : (
                    <span className="text-center text-3xl font-black tracking-tight text-slate-800">
                      {logo.alt}
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                  <span className="text-sm font-bold text-slate-800">{logo.alt}</span>
                  <span className="text-[11px] font-semibold text-slate-400">Xem sản phẩm</span>
                </div>
              </Link>
            ))}
          </div>

          <div className="grid border-t border-slate-200 bg-slate-50 text-center text-xs font-semibold text-slate-600 sm:grid-cols-3">
            <div className="border-b border-slate-200 px-4 py-3 sm:border-b-0 sm:border-r">
              Linh kiện PC hiệu năng cao
            </div>
            <div className="border-b border-slate-200 px-4 py-3 sm:border-b-0 sm:border-r">
              Hàng hóa kiểm tra trước khi giao
            </div>
            <div className="px-4 py-3">Hỗ trợ bảo hành và nâng cấp</div>
          </div>
        </div>
      </div>
    </section>
  );
}
